import { useState, useEffect } from 'react'
import CustomTable from '../../components/CustomTable'
import FilterModal from '../../components/FilterModal'
import Api from '../../api/endPoints'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setBreadcrumb } from '../../features/breadcrumb/breadcrumbSlice'
import { selectAssetProfileId, selectAssetProfileStatus, fetchAssetProfile, resetAssetProfile } from '../../features/assetProfile/assetProfileSlice'
import { formatDateTime } from '../../utils/date'
import { flattenOrgOptions } from '../../utils/organizations'
import { headerLabel } from '../../utils/columns'
import { escapeCsvCell } from '../../utils/csv'

const StatusBadge = ({ status }) => {
  const isActive = status === 'Active'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isActive ? 'bg-[#daf5ed] text-[#006c4d]' : 'bg-[#eceded] text-[#5e6673]'}`}>
      {status}
    </span>
  )
}

// Column key -> the sortBy value the backend expects for that column.
const SORT_KEYS = {
  bmsId: '_id',
  status: 'online',
  soc: 'soc',
  vimId: 'deviceId',
  lastDataReceivedAt: 'lastSeen',
}

// `unit` is rendered into the header by CustomTable, not onto every cell. The
// widths are floors rather than fixed sizes (fitColumnsToContent below), so a
// header like "Pack Voltage (V)" claims the room it needs instead of being
// truncated at a width picked before the unit was there. The four measurement
// columns have no `render`: their values are plain, so CustomTable's own "--"
// covers a missing reading.
const COLUMNS = [
  { key: 'bmsId', label: 'BMS ID', width: '140px', sortable: true },
  { key: 'status', label: 'Status', width: '100px', sortable: true, render: (row) => <StatusBadge status={row.status ? 'Active' : 'Inactive'} />},
  { key: 'soc', label: 'SoC', unit: '%', width: '80px', sortable: true },
  { key: 'soh', label: 'SoH', unit: '%', width: '80px' },
  { key: 'packVoltage', label: 'Pack Voltage', unit: 'V', width: '110px' },
  { key: 'current', label: 'Current', unit: 'A', width: '100px' },
  { key: 'organization', label: 'Organization', width: '170px' },
  { key: 'vimId', label: 'VIM ID', width: '200px', sortable: true },
  { key: 'lastDataReceivedAt', label: 'Last Data Received at', sortable: true },
]

// Maps a /nsync/bms item onto the table's row shape. Pack voltage is the sum of
// the latest cell voltages, converted from mV to V — the column header says V.
// Current keeps its explicit `+` sign, which reads as direction (charge vs
// discharge) rather than as a unit.
const mapBmsItem = (item) => {
  const latest = item.latest ?? {}
  const packVoltage_mV = Array.isArray(latest.cells) ? latest.cells.reduce((sum, mv) => sum + mv, 0) : null
  return {
    bmsId: item._id,
    soc: latest.soc_pct,
    soh: latest.soh_pct,
    packVoltage: packVoltage_mV != null ? (packVoltage_mV / 1000).toFixed(1) : undefined,
    current: latest.current_A != null ? `${latest.current_A > 0 ? '+' : ''}${latest.current_A}` : undefined,
    status: item.online,
    organization: item.orgName ?? '-',
    vimId: item.deviceId,
    lastDataReceivedAt: formatDateTime(item.lastSeen),
  }
}

const BmsList = () => {
  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [devicesData, setDevicesData] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [filters, setFilters] = useState({ status: [], organization: [] })
  // Sorting is done by the backend. We track which column is sorted and its
  // direction, map the column to its backend sortBy key (SORT_KEYS), and
  // refetch whenever either changes. Defaults to newest-first by last seen.
  const [sortColumn, setSortColumn] = useState('lastDataReceivedAt')
  const [sortDirection, setSortDirection] = useState('desc')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Bumped by the error state's Retry button to re-run the fetch effect.
  const [reloadKey, setReloadKey] = useState(0)
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user)
  // Fetched once per session by Layout — null until it resolves, which is why
  // the fetch effect below waits on it.
  const profileID = useSelector(selectAssetProfileId)
  const profileStatus = useSelector(selectAssetProfileStatus)
  // The org whose linked customers make up the Organization filter. Undefined
  // until /user/me resolves, which is why the fetch effect below waits on it.
  const organizationId = user?.role?.organizationId

  const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
  ]

  useEffect(() => {
    dispatch(setBreadcrumb([{ title: 'BMS List', link: '/bms-list', current: true }]))
  }, [dispatch])

  // Status and organization filtering are handled by the backend
  // (status=active|inactive maps to the node's online flag; orgId filters by
  // organization). The API accepts a single value per param, so each is only
  // passed when exactly one option is selected — none selected means no filter.
  const statusFilter = filters.status.length === 1 ? filters.status[0].toLowerCase() : undefined
  const orgFilter = filters.organization.length === 1 ? filters.organization[0] : undefined

  // max/totalPages come straight from the backend (it now does real
  // offset/limit pagination) — no need to estimate or derive them locally.
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  // Sorting a column refetches, so overlapping requests are routine here:
  // `cancelled` keeps a superseded response from overwriting fresher rows or
  // clearing the loading flag while the newer request is still open.
  useEffect(() => {
    let cancelled = false

    // No profile id means no query to make. A failed profile load has to
    // surface as an error, otherwise the table would sit on its spinner
    // forever with nothing explaining why.
    if (profileID == null) {
      if (profileStatus === 'failed') {
        setError("Couldn't load BMS devices")
        setIsLoading(false)
      } else {
        setIsLoading(true)
      }
      return
    }

    const fetchBmsDevices = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await Api.getBmsDevices(profileID, statusFilter, orgFilter, SORT_KEYS[sortColumn], sortDirection, currentPage, itemsPerPage)
        if (cancelled) return
        // `request` resolves for every status instead of throwing, so an HTTP
        // error never reaches the catch below — without this check a 500 would
        // read as "no devices" rather than as a failure.
        if (response.status !== 200) {
          console.error('BMS devices request failed with status', response.status)
          setError("Couldn't load BMS devices")
          return
        }
        const data = response.data ?? {}
        setDevicesData((data.items ?? []).map(mapBmsItem))
        setTotalItems(data.max ?? 0)
        setTotalPages(data.totalPages ?? 1)
      } catch (error) {
        console.error('Error fetching BMS devices:', error)
        // Without this the table would look identical to a genuinely empty
        // fleet — the one reading an operator must not be given by mistake.
        if (!cancelled) setError("Couldn't load BMS devices")
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchBmsDevices()
    return () => { cancelled = true }
  }, [profileID, profileStatus, statusFilter, orgFilter, sortColumn, sortDirection, currentPage, itemsPerPage, reloadKey])

  useEffect(() => {
    if (organizationId == null) return
    let cancelled = false

    const fetchLinkedCustomers = async () => {
      try {
        const { status, data } = await Api.getProfileLinkedCustomers(organizationId)
        if (cancelled) return
        // options show the org name; the id is what gets sent as orgId
        const options = flattenOrgOptions(data?.list ?? data)
        if (status === 200 && options.length > 0) {
          setOrgOptions(options)
        }
      } catch (error) {
        console.error('Failed to fetch linked customers:', error)
      }
    }

    fetchLinkedCustomers()
    return () => { cancelled = true }
  }, [organizationId])

  // The profile id is what the device query is built from, so when *it* is what
  // failed, bumping reloadKey alone would retry nothing — reset it back to idle
  // so the thunk's once-per-session guard lets a fresh attempt through.
  const handleRetry = () => {
    if (profileStatus === 'failed') {
      dispatch(resetAssetProfile())
      dispatch(fetchAssetProfile())
    }
    setReloadKey((n) => n + 1)
  }

  const filteredDevices = devicesData.filter((row) => {
    if (search.trim()) {
      const query = search.trim().toLowerCase()
      const matchesBmsId = String(row.bmsId ?? '').toLowerCase().includes(query)
      const matchesVimId = String(row.vimId ?? '').toLowerCase().includes(query)
      if (!matchesBmsId && !matchesVimId) return false
    }
    return true
  })

  // Sorting AND pagination are both handled by the backend now — the list
  // arrives as exactly one page already, so this only search-filters it.
  const pagedDevices = filteredDevices

  const handleDownloadCsv = () => {
    // Headers come from COLUMNS so they carry the same units as the table and
    // can't drift from it. The row cells below are in COLUMNS order; only Status
    // differs from the raw value, since its column renders a badge.
    const headers = COLUMNS.map(headerLabel)
    const rows = filteredDevices.map((row) => [
      row.bmsId,
      row.status ? 'Active' : 'Inactive',
      row.soc,
      row.soh,
      row.packVoltage,
      row.current,
      row.organization,
      row.vimId,
      row.lastDataReceivedAt,
    ])
    const csv = [headers, ...rows].map((cells) => cells.map(escapeCsvCell).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'bms-device-list.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // const totalItems = filteredDevices.length
  // const totalPages = Math.ceil(totalItems / itemsPerPage)
  // const start = (currentPage - 1) * itemsPerPage
  // const pagedDevices = filteredDevices.slice(start, start + itemsPerPage)

  return (
    <div className="bg-[#f9fafa] flex flex-col items-start overflow-hidden rounded h-full w-full">
      <div className="flex items-center gap-4 px-4 py-3 w-full shrink-0">
        <span className="flex-1 text-sm font-semibold text-[#252a34]">
          BMS Device List
        </span>
        {showSearch ? (
          <div className="flex items-center gap-2 h-9 px-2 rounded border border-[#d8dde6] bg-white w-[260px]">
            <span className="material-icons text-xl text-[#949cac]">search</span>
            <input
              autoFocus
              className="flex-1 min-w-0 outline-none border-none text-sm text-[#252a34] placeholder:text-[#949cac] bg-transparent"
              placeholder="Search BMS ID or VIM ID"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
            />
            <button
              className="border-none bg-transparent cursor-pointer flex items-center justify-center p-0"
              onClick={() => {
                setSearch('')
                setShowSearch(false)
                setCurrentPage(1)
              }}
            >
              <span className="material-icons text-base text-[#949cac]">close</span>
            </button>
          </div>
        ) : (
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-full hover:bg-[#eceded]"
            onClick={() => setShowSearch(true)}
          >
            <span className="material-icons text-xl text-[#272d37]">search</span>
          </button>
        )}
        <FilterModal
          tabs={[
            {
              key: "status",
              label: "Status",
              type: "static",
              options: STATUS_OPTIONS,
            },
            {
              key: "organization",
              label: "Organization",
              type: "searchable",
              options: orgOptions,
            },
          ]}
          value={filters}
          onApply={(next) => {
            setFilters(next);
            setCurrentPage(1);
          }}
        />
        <button
          className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-full"
          onClick={handleDownloadCsv}
        >
          <span className="material-symbols-outlined text-xl text-[#272d37]">
            file_save
          </span>
        </button>
      </div>

      <CustomTable
        columns={COLUMNS}
        data={pagedDevices}
        isLoading={isLoading}
        error={error}
        onRetry={handleRetry}
        loadingLabel="Loading BMS devices…"
        emptyMessage={search.trim() ? 'No BMS devices match this search' : 'No BMS devices found'}
        fitColumnsToContent
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={(key) => {
          if (key === sortColumn) {
            setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'))
          } else {
            setSortColumn(key)
            setSortDirection('asc')
          }
          setCurrentPage(1)
        }}
        onRowClick={(row) =>
          navigate(`/ViewDashboard/${row.vimId}/bms/${row.bmsId}`)
        }
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage,
          onPageChange: setCurrentPage,
          onItemsPerPageChange: setItemsPerPage,
        }}
      />
    </div>
  );
}

export default BmsList
